import { Intent } from '@blueprintjs/core';
import { FormCheckbox, FormLocationSelector, FormTextInput } from 'components/form-fields';
import { ListingTable } from 'components/listing';
import { NotificationToaster } from 'components/notifications';
import { useDebounce } from 'hooks/useDebounce';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { EmployeeService } from 'services';

const defaultArray = [];

const debounceTimeout = 750;
const defaultRecordSize = 25;
const nameHeader = "Name";

const tableHeaders = [nameHeader, "Location", "Reference", "Select"];
const sortableHeaders = [nameHeader, "Location", "Reference"];

// The employee picker of the new-review wizard: the locations the manager may pick from, a search
// over the employees within them, and the single employee the review will be raised for.
export function EmployeeStep(
    {
        selectedEmployeeId = null,
        selectedLocations = defaultArray,
        searchTerm = "",
        useLocationDefaults = true,
        onSelectedEmployeeChange,
        onSelectedLocationsChange,
        onSearchTermChange
    }
) {
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [employees, setEmployees] = useState(defaultArray);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultRecordSize);
    const [sortedBy, setSortedBy] = useState(nameHeader);
    const [sortedDir, setSortedDir] = useState('A');
    const [loadingData, setLoadingData] = useState(false);

    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    function clearResults() {
        setEmployees(defaultArray);
        setTotalRecords(0);
        setLoadingData(false);
    }

    // isActive reports whether the search is still the step's own - a search made under a selection
    // the manager has since changed must not write its employees over the selection now in force.
    function loadEmployees(isActive) {
        // A manager may only raise a review for an employee they are entitled to see, and the
        // locations they pick are what bounds that. With none picked there is nothing to bound
        // the search by, so no search is made and no employee is offered.
        if (selectedLocations.length === 0) {
            clearResults();
            return;
        }

        setLoadingData(true);

        const request = {
            ignoreLocationPermissions: false,
            requestCount: pageSize,
            pageNumber: pageNumber,
            searchTerm: debouncedSearchTerm,
            sortField: sortedBy.toLowerCase() === 'name' ? 'surname' : sortedBy.toLowerCase(),
            sortDirection: sortedDir,
            filters: selectedLocations.map((locationId) => ({ key: 'LocationId', value: locationId })),
            LocationFiltersAsIds: true
        };

        EmployeeService.searchEmployees(request).then(
            (result) => {
                if (!isActive()) {
                    return;
                }

                setEmployees(result?.data ?? defaultArray);
                setTotalRecords(result?.totalCount ?? 0);
                setLoadingData(false);
            },
            (error) => {
                if (!isActive()) {
                    return;
                }

                clearResults();
                NotificationToaster.show(Intent.DANGER, error?.message, true);
            }
        );
    }

    useEffect(() => {
        let active = true;

        loadEmployees(() => active);

        return () => {
            active = false;
        };
    }, [selectedLocations, debouncedSearchTerm, pageNumber, pageSize, sortedBy, sortedDir]);

    function onSelect(employeeId) {
        // A review is raised for one employee, so ticking a row replaces the selection and
        // re-ticking the selected row clears it.
        onSelectedEmployeeChange(selectedEmployeeId === employeeId ? null : employeeId);
    }

    function onSearchChange(event) {
        setPageNumber(1);
        onSearchTermChange(event.target.value);
    }

    function onLocationsChange(locations) {
        setPageNumber(1);
        onSelectedLocationsChange(locations);
    }

    function onSort(header, direction) {
        setSortedBy(header);
        setSortedDir(direction);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    const tableRows = employees.map((employee) => [
        `${employee.surname}, ${employee.firstName}`,
        employee.location?.name,
        employee.reference,
        <FormCheckbox
            key={`select-employee-${employee.employeeId}`}
            id={`select-employee-${employee.employeeId}`}
            checked={selectedEmployeeId === employee.employeeId}
            onChange={() => onSelect(employee.employeeId)}
        />
    ]);

    return (
        <div>
            <h2>Employees</h2>
            <div className="spacer-bottom-small">
                <div className="filter-controls">
                    <div className="inline-items">
                        <div className="inline-item fill">
                            <div className="form-field-full">
                                <FormTextInput
                                    id="new-review-employee-search"
                                    placeholder="Search for employee"
                                    value={searchTerm}
                                    onChange={onSearchChange}
                                    large
                                    icon="search"
                                />
                            </div>
                        </div>
                        <div className="inline-item">
                            <FormLocationSelector
                                businessArea={"Tacho"}
                                selectedLocations={selectedLocations}
                                setSelectedLocations={onLocationsChange}
                                useHierarchy={useHierarchy}
                                setUseHierarchy={setUseHierarchy}
                                useLocationDefaults={useLocationDefaults}
                                locationDefaultsToTrue={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ListingTable
                id="new-review-employees"
                headers={tableHeaders}
                data={tableRows}
                totalRecordCount={totalRecords}
                loadingData={loadingData}
                sortable
                sortableHeaders={sortableHeaders}
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                onSort={onSort}
                pageSize={pageSize}
                onPagingChange={onPagingChange}
                noDataHeading="No employees found"
                noDataMessage="There are no employees for the selected locations and search text."
                fixedHeight
                showPerPage={false}
            />
        </div>
    );
}

EmployeeStep.propTypes = {
    selectedEmployeeId: PropTypes.number,
    selectedLocations: PropTypes.array,
    searchTerm: PropTypes.string,
    useLocationDefaults: PropTypes.bool,
    onSelectedEmployeeChange: PropTypes.func.isRequired,
    onSelectedLocationsChange: PropTypes.func.isRequired,
    onSearchTermChange: PropTypes.func.isRequired
};
